import React from 'react';
import {useTranslation} from 'react-i18next';
import {FaIcon} from '../../FaIcon';
import {tabUrl} from './sheetUrl';

// Empty state del Media Plan cuando NO hay propuesta, con una variante por MOTIVO.
//
// Antes había una sola pantalla ("Todavía no hay propuesta") que no distinguía "todavía no leímos"
// de "leímos y la solapa no sirve", y no ofrecía salida. En el kickoff de Rouge eso terminó con la
// conexión en verde, la pantalla en blanco y la reunión cortada a la mitad: el backend sabía que la
// solapa elegida era una hoja de seguimiento, pero el motivo moría en un log.
//
// Se copia la ARQUITECTURA de `DataExplorerEmptyState` (kinds congelados + configs + un solo
// render) pero no el componente: su SCSS está namespaceado al Explorador, su contrato de acciones
// (un href + un retry) no cubre dos CTAs contextuales por variante, y su caja es de pantalla
// completa — acá el empty state vive dentro del panel, debajo de un strip que debe seguir leyéndose.
//
// Regla de color: ámbar `$bad` = "hay algo para resolver y lo podés resolver"; azul `$normal` =
// informativo. **Rojo NUNCA acá**: se reserva para la conexión caída (ERROR/REVOKED), que es lo
// verdaderamente roto. Si un problema de solapa se pintara de rojo, el día que se corte el acceso
// de verdad nadie lo distinguiría.

const NS = 'media_plan.proposal';

export const MP_EMPTY_KINDS = Object.freeze({
    NOT_GENERATED_YET: 'not_generated_yet',
    NO_TAB_FOR_PERIOD: 'no_tab_for_period',
    TAB_NOT_A_PLAN: 'tab_not_a_plan',
    STACKED_PLANS: 'stacked_plans',
    CURRENCY_MIX: 'currency_mix',
    READ_FAILED: 'read_failed',
});

// Acciones posibles. Cuál es primaria depende del motivo: releer sólo tiene sentido si el próximo
// movimiento del usuario es fuera de Dashbo o reintentar algo transitorio. Cuando el diagnóstico es
// "la solapa está mal", releer la MISMA solapa devuelve el mismo resultado — que es exactamente el
// callejón que se vivió desconectando y reconectando la planilla.
export const MP_EMPTY_ACTIONS = Object.freeze({
    GENERATE: 'generate',       // leer el plan (primera lectura, sin forzar)
    REREAD: 'reread',           // volver a leer forzando (reintento de una lectura fallida)
    PICK_TAB: 'pick_tab',       // abrir el selector de solapas
    OPEN_SHEET: 'open_sheet',   // ir a la planilla (el arreglo está allá)
});

const CONFIGS = {
    [MP_EMPTY_KINDS.NOT_GENERATED_YET]: {
        tone: '-info', icon: 'wand-magic-sparkles',
        primary: MP_EMPTY_ACTIONS.GENERATE, secondary: MP_EMPTY_ACTIONS.PICK_TAB,
    },
    [MP_EMPTY_KINDS.NO_TAB_FOR_PERIOD]: {
        tone: '-warning', icon: 'calendar-xmark',
        primary: MP_EMPTY_ACTIONS.PICK_TAB, secondary: MP_EMPTY_ACTIONS.OPEN_SHEET,
    },
    [MP_EMPTY_KINDS.TAB_NOT_A_PLAN]: {
        tone: '-warning', icon: 'chart-line',
        primary: MP_EMPTY_ACTIONS.PICK_TAB, secondary: MP_EMPTY_ACTIONS.OPEN_SHEET,
    },
    [MP_EMPTY_KINDS.STACKED_PLANS]: {
        tone: '-warning', icon: 'layer-group',
        primary: MP_EMPTY_ACTIONS.PICK_TAB, secondary: MP_EMPTY_ACTIONS.OPEN_SHEET,
    },
    [MP_EMPTY_KINDS.CURRENCY_MIX]: {
        // Única variante donde la acción primaria sale de Dashbo: el arreglo está en la planilla.
        tone: '-warning', icon: 'coins',
        primary: MP_EMPTY_ACTIONS.OPEN_SHEET, secondary: MP_EMPTY_ACTIONS.PICK_TAB,
    },
    [MP_EMPTY_KINDS.READ_FAILED]: {
        tone: '-warning', icon: 'triangle-exclamation',
        primary: MP_EMPTY_ACTIONS.REREAD, secondary: MP_EMPTY_ACTIONS.PICK_TAB,
    },
};

// Motivos de descarte, en orden de "qué le explico primero al usuario". Si varias candidatas
// fallaron por cosas distintas, gana la que tiene una acción más concreta detrás.
const REASON_TO_KIND = {
    STACKED_PLANS: MP_EMPTY_KINDS.STACKED_PLANS,
    CURRENCY_MIX: MP_EMPTY_KINDS.CURRENCY_MIX,
    EXTRACTION_FAILED: MP_EMPTY_KINDS.READ_FAILED,
    TRACKING_SHEET: MP_EMPTY_KINDS.TAB_NOT_A_PLAN,
    NOT_A_PLAN: MP_EMPTY_KINDS.TAB_NOT_A_PLAN,
};
const REASON_PRIORITY = ['STACKED_PLANS', 'CURRENCY_MIX', 'EXTRACTION_FAILED', 'TRACKING_SHEET',
                         'NOT_A_PLAN'];

/**
 * Escaneo del backend → variante de empty state. Puro y sin React a propósito: es la parte con
 * lógica y así se testea sin montar nada.
 *
 * Sin escaneo (conexión nueva, o backend anterior al deploy) devuelve `NOT_GENERATED_YET`, que
 * ofrece leer el plan. El contrato es que un dato faltante degrada a "todavía no", nunca a un error.
 */
export function emptyKindFromScan(scan) {
    if (!scan || !scan.outcome) return MP_EMPTY_KINDS.NOT_GENERATED_YET;
    if (scan.outcome === 'NEVER_SCANNED') return MP_EMPTY_KINDS.NOT_GENERATED_YET;
    if (scan.outcome === 'READ_FAILED') return MP_EMPTY_KINDS.READ_FAILED;
    if (scan.outcome === 'NO_TABS' || scan.outcome === 'NO_TAB_FOR_MONTH') {
        return MP_EMPTY_KINDS.NO_TAB_FOR_PERIOD;
    }
    const codes = new Set((scan.reasons || []).map((r) => r.code));
    const worst = REASON_PRIORITY.find((code) => codes.has(code));
    if (worst) return REASON_TO_KIND[worst];
    // `OK` sin propuesta y sin motivos: la lectura corrió y no produjo nada explicable. Ofrecer
    // leer de nuevo es la salida menos mala; el selector queda como secundaria.
    return MP_EMPTY_KINDS.NOT_GENERATED_YET;
}

// La solapa cuyo problema estamos explicando: la elegida, o la primera descartada con motivo.
function subjectTab(scan, kind) {
    if (!scan) return null;
    if (scan.selected && kind !== MP_EMPTY_KINDS.NO_TAB_FOR_PERIOD) return scan.selected;
    const codes = REASON_PRIORITY.filter((code) => REASON_TO_KIND[code] === kind);
    const reason = (scan.reasons || []).find((r) => codes.includes(r.code));
    return reason ? {gid: reason.tab_gid, title: reason.tab_title} : null;
}

// Otras candidatas del mismo mes que NO son la que se leyó: son la salida real del caso Rouge — en
// el kickoff, la solapa correcta estaba ahí y habría estado a un clic.
function otherCandidates(scan) {
    if (!scan) return [];
    const selectedGid = (scan.selected || {}).gid;
    return (scan.tabs || []).filter((tab) => (
        tab.plan_month && tab.plan_month === scan.target_month && tab.gid !== selectedGid
        && tab.role !== 'IGNORED'
    ));
}

export function MediaPlanEmptyState({scan, externalId, generating, onGenerate, onReread,
                                     onPickTab, onUseTab, repeatedFailure}) {
    const {t} = useTranslation();
    const kind = emptyKindFromScan(scan);
    const config = CONFIGS[kind] || CONFIGS[MP_EMPTY_KINDS.NOT_GENERATED_YET];
    const tab = subjectTab(scan, kind);
    const candidates = otherCandidates(scan);
    const counts = (scan || {}).counts || {};
    const values = {
        tab: (tab || {}).title || '',
        period: (scan || {}).target_month || '',
        total: counts.total || 0,
        probed: counts.probed || 0,
        interpolation: {escapeValue: false},
    };

    const renderAction = (action, className) => {
        if (!action) return null;
        if (action === MP_EMPTY_ACTIONS.OPEN_SHEET) {
            const href = tabUrl(externalId, (tab || {}).gid);
            if (!href) return null;
            return (
                <a className={className} href={href} target="_blank" rel="noopener noreferrer">
                    {t(`${NS}.empty.action.open_sheet`)}
                    <FaIcon iconName="up-right-from-square" className="mp-empty-action-ext"/>
                </a>
            );
        }
        const label = {
            [MP_EMPTY_ACTIONS.GENERATE]: generating ? t(`${NS}.generating`) : t(`${NS}.generate`),
            [MP_EMPTY_ACTIONS.REREAD]: generating ? t(`${NS}.generating`) : t(`${NS}.empty.action.reread`),
            [MP_EMPTY_ACTIONS.PICK_TAB]: t(`${NS}.empty.action.pick_tab`),
        }[action];
        const onClick = {
            [MP_EMPTY_ACTIONS.GENERATE]: onGenerate,
            [MP_EMPTY_ACTIONS.REREAD]: onReread,
            [MP_EMPTY_ACTIONS.PICK_TAB]: onPickTab,
        }[action];
        if (!onClick) return null;
        const busy = generating && action !== MP_EMPTY_ACTIONS.PICK_TAB;
        return (
            // Envuelto a propósito: pasar el handler pelado le entregaría el evento de click como
            // primer argumento, y `onPickTab` lo interpretaría como el gid a preseleccionar.
            <button type="button" className={className} disabled={busy} onClick={() => onClick()}>
                {label}
            </button>
        );
    };

    return (
        <div className={`mp-empty ${config.tone}`}>
            <div className="mp-empty-icon">
                <FaIcon iconName={config.icon}/>
            </div>
            <h3 className="mp-empty-title">{t(`${NS}.empty.${kind}.title`, values)}</h3>
            <p className="mp-empty-desc">{t(`${NS}.empty.${kind}.desc`, values)}</p>

            <div className="mp-empty-actions">
                {renderAction(config.primary, 'btn -alt')}
                {renderAction(config.secondary, 'btn -alt -negative')}
            </div>

            {/* Anti-loop: releer la misma solapa devuelve el mismo resultado. Si el usuario ya lo
                intentó y volvió a fallar por lo mismo, decírselo en vez de dejarlo dando vueltas. */}
            {repeatedFailure && (
                <p className="mp-empty-hint">{t(`${NS}.empty.same_result_hint`)}</p>
            )}

            {/* Las otras candidatas del mes, con su motivo: es el atajo a la solapa correcta. */}
            {candidates.length > 0 && onUseTab && (
                <div className="mp-empty-notes">
                    <div className="mp-tab-candidates">
                        <span className="mp-tab-candidates-title">
                            {t(`${NS}.empty.candidates_title`, values)}
                        </span>
                        {candidates.map((candidate) => (
                            <div key={candidate.gid} className="mp-tab-candidate">
                                <span className="mp-tab-candidate-main">
                                    <span className="mp-tab-candidate-name" title={candidate.title}>
                                        {candidate.title}
                                    </span>
                                    <span className="mp-tab-candidate-reason">
                                        {t(`media_plan.connections.tab.reason.${candidate.reason}`,
                                            {defaultValue: t(`${NS}.empty.candidate_unread`)})}
                                    </span>
                                </span>
                                <button type="button" className="btn -alt -negative mp-btn-sm"
                                        onClick={() => onUseTab(candidate)}>
                                    {t(`${NS}.empty.candidate_use`)}
                                </button>
                                {tabUrl(externalId, candidate.gid) && (
                                    <a className="mp-tab-candidate-link"
                                       href={tabUrl(externalId, candidate.gid)}
                                       target="_blank" rel="noopener noreferrer"
                                       title={t(`media_plan.connections.tab.open`)}>
                                        <FaIcon iconName="up-right-from-square"/>
                                    </a>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
